'use client'

import React from 'react'
import styles from './feedback.module.css'

interface SafeSpinnerProps {
  size?: 'small' | 'default' | 'large'
  color?: string
  className?: string
  /** Accessibility label */
  'aria-label'?: string
}

const SIZE_MAP = {
  small: 16,
  default: 24,
  large: 32,
} as const

/**
 * Safe spinner — no Ant Design Spin, no styled-jsx.
 * Prevents EllipsisMeasure loops and SSR/client hydration class-hash mismatches.
 */
export function SafeSpinner({
  size = 'default',
  color,
  className = '',
  'aria-label': ariaLabel = 'Loading',
}: SafeSpinnerProps) {
  const spinnerSize = SIZE_MAP[size]
  const strokeWidth = size === 'small' ? 2 : size === 'large' ? 3 : 2.5

  return (
    <div
      className={`${styles.spinnerRoot} ${className}`.trim()}
      style={
        {
          '--spinner-size': `${spinnerSize}px`,
          '--spinner-stroke': String(strokeWidth),
          '--spinner-duration': '1s',
          color: color || 'currentColor',
        } as React.CSSProperties
      }
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      aria-busy="true"
    >
      <span className={styles.spinnerBox} aria-hidden="true">
        <svg className={styles.spinnerSvg} viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}>
          <circle
            className={styles.spinnerCircle}
            cx={spinnerSize / 2}
            cy={spinnerSize / 2}
            r={Math.max(spinnerSize / 2 - strokeWidth, 1)}
          />
        </svg>
      </span>
    </div>
  )
}

export default SafeSpinner

/** Alias for backward compatibility */
export const TradingSpinner = SafeSpinner
